////////////////////////////////////////////
// WALLET REGISTRY — THE ONE CURATED LIST OF WALLET APPS (BOTH CHAINS)
////////////////////////////////////////////
//
// THE INDUSTRY PATTERN (RAINBOWKIT ET AL): DISCOVER INSTALLED WALLETS AT
// RUNTIME (EIP-6963 / WALLET-STANDARD) AND BACK THEM WITH AN IN-REPO CURATED
// BRAND LIST FOR EVERYTHING ELSE — INSTALL LINKS, BRAND MARKS, AND MOBILE
// DEEPLINK CAPABILITY. THIS FILE IS THAT LIST: VERSIONED WITH THE CODE, NO
// HOSTED REGISTRY DEPENDENCY. ICONS ARE OFFICIAL MARKS FETCHED ONCE FROM THE
// REOWN EXPLORER REGISTRY INTO public/wallets/. ADD A WALLET = ADD ONE ENTRY.
import type { DlProvider } from "@/lib/wallet/deeplink/store";

export interface WalletBrand {
  id: string;
  name: string;
  // EVERY CHAIN THE APP GENUINELY SERVES — MULTICHAIN WALLETS (METAMASK,
  // TRUST, OKX, COINBASE) LIST ON BOTH SIDES; EVM MEMBERSHIP ALSO REQUIRES
  // CUSTOM-CHAIN SUPPORT (ROBINHOOD CHAIN ISN'T A PRESET ANYWHERE)
  chains: ("solana" | "evm")[];
  icon: string;
  color: string;
  installUrl: string;
  // SUPPORTS THE ul/v1 ENCRYPTED DEEPLINK PROTOCOL (MOBILE NATIVE-BROWSER CONNECT)
  dl?: DlProvider;
  // THE WALLET'S DOCUMENTED WALLETCONNECT UNIVERSAL LINK (…/wc?uri=) — MAKES
  // THE APP CONNECTABLE FROM A MOBILE BROWSER, WHERE NOTHING CAN INJECT
  wcLink?: string;
}

const WALLETS: WalletBrand[] = [
  // SOLANA-FIRST — PHANTOM/SOLFLARE ARE THE ul/v1 DEEPLINK APPS. PHANTOM'S
  // EVM SIDE IS A FIXED NETWORK LIST (ETHEREUM/BASE/POLYGON/MONAD/HYPEREVM;
  // "Can I manually add a network to Phantom?" — NO, PER PHANTOM HELP), SO
  // IT CAN NEVER REACH ROBINHOOD CHAIN → NO evm MEMBERSHIP; SAME FOR BACKPACK
  { id: "phantom", name: "Phantom", chains: ["solana"], icon: "/wallets/phantom.png", color: "#AB9FF2", installUrl: "https://phantom.app/download", dl: "phantom" },
  { id: "solflare", name: "Solflare", chains: ["solana"], icon: "/wallets/solflare.png", color: "#FC8E2C", installUrl: "https://solflare.com/download", dl: "solflare" },
  { id: "backpack", name: "Backpack", chains: ["solana"], icon: "/wallets/backpack.png", color: "#E33E3F", installUrl: "https://backpack.app/download" },
  // MULTICHAIN + EVM — THE MULTICHAIN APPS SHIP NATIVE SOLANA SUPPORT, SO
  // THEY LIST ON BOTH SIDES (INSTALLED EXTENSIONS SELF-REGISTER VIA
  // WALLET-STANDARD AND SURFACE AS detected ON SOLANA). METAMASK HAS NO
  // wcLink ON PURPOSE — IT CONNECTS VIA ITS OFFICIAL SDK CONNECTOR (ITS WC
  // DEEPLINK PATH IS BROKEN ON ANDROID, metamask-mobile #5212). wcLink ONLY
  // WHERE THE WALLET DOCUMENTS A STABLE /wc UNIVERSAL LINK; COINBASE/OKX
  // ROUTE PAIRING THROUGH THEIR OWN SDKS, SO THEY STAY INSTALL-ONLY ON
  // MOBILE RATHER THAN SHIPPING A DEAD ROW
  { id: "metamask", name: "MetaMask", chains: ["evm", "solana"], icon: "/wallets/metamask.png", color: "#F6851B", installUrl: "https://metamask.io/download" },
  { id: "rainbow", name: "Rainbow", chains: ["evm"], icon: "/wallets/rainbow.png", color: "#001E59", installUrl: "https://rainbow.me/download", wcLink: "https://rnbwapp.com/wc?uri=" },
  { id: "coinbase", name: "Coinbase Wallet", chains: ["evm", "solana"], icon: "/wallets/coinbase.png", color: "#0052FF", installUrl: "https://www.coinbase.com/wallet/downloads" },
  { id: "trust", name: "Trust Wallet", chains: ["evm", "solana"], icon: "/wallets/trust.png", color: "#0500FF", installUrl: "https://trustwallet.com/download", wcLink: "https://link.trustwallet.com/wc?uri=" },
  { id: "okx", name: "OKX Wallet", chains: ["evm", "solana"], icon: "/wallets/okx.png", color: "#FFFFFF", installUrl: "https://web3.okx.com/download" },
];

export const SOLANA_WALLETS = WALLETS.filter((w) => w.chains.includes("solana"));
export const EVM_WALLETS = WALLETS.filter((w) => w.chains.includes("evm"));
export const DL_WALLETS = SOLANA_WALLETS.filter((w) => w.dl);
export const WC_EVM_WALLETS = EVM_WALLETS.filter((w) => w.wcLink);

// MATCH A RUNTIME-DISCOVERED WALLET NAME TO ITS CURATED BRAND — STRICT ON
// PURPOSE: LOOSE SUBSTRING MATCHING MISLABELS LOOK-ALIKES ("Trustee Wallet"
// IS NOT TRUST WALLET) AND WOULD SUPPRESS GENUINE INSTALL ROWS. EVERY
// CURATED WALLET ANNOUNCES ITSELF UNDER EXACTLY ITS BRAND NAME.
export function brandFor(name: string | undefined | null): WalletBrand | undefined {

  if (!name)
    return undefined;
  const n = name.trim().toLowerCase();
  return WALLETS.find((w) => n === w.id || n === w.name.toLowerCase());
}

export function brandByDl(provider: DlProvider): WalletBrand {

  return DL_WALLETS.find((w) => w.dl === provider)!;
}
